import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmt = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const isSugarTruck = (bookingType) => {
  if (!bookingType) return false;
  const t = bookingType.toLowerCase();
  return (
    t.includes('sugar + vehicle bundle') ||
    t.includes('bundle') ||
    t.includes('sugar_truck') ||
    t.includes('sugar+truck') ||
    t.includes('sugar + truck')
  );
};

const RAZORPAY_KEY = 'rzp_live_SyswhY6CmR3gMx';

// ─── sub-components ──────────────────────────────────────────────────────────

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between items-center py-2 border-b last:border-0" style={{ borderColor: '#f3f0ea' }}>
    <span className="text-xs font-medium" style={{ color: '#9ca3af' }}>{label}</span>
    <span className="text-xs font-semibold text-right ml-4" style={{ color: '#1f2937', maxWidth: '60%', wordBreak: 'break-all' }}>{value || '—'}</span>
  </div>
);

const TollTimeline = ({ crossings, loading }) => {
  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="w-6 h-6 border-3 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
        <p className="text-xs" style={{ color: '#9ca3af' }}>Loading toll crossings...</p>
      </div>
    );
  }

  if (!crossings || crossings.length === 0) {
    return (
      <div className="text-center py-8 rounded-xl" style={{ background: '#fdf8f0', border: '1px dashed #d6c9b8' }}>
        <Icon name="MapPin" size={28} color="#d1a87a" />
        <p className="mt-2 text-sm font-medium" style={{ color: '#6b7280' }}>No toll crossings recorded yet</p>
        <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>FASTag data appears here as the vehicle passes toll plazas</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* vertical line */}
      <div className="absolute left-4 top-4 bottom-4 w-0.5" style={{ background: '#e8ddd0' }} />
      <div className="space-y-4">
        {crossings.map((c, i) => (
          <div key={c.id || i} className="relative flex gap-4 items-start pl-10">
            {/* dot */}
            <div
              className="absolute left-2.5 w-3 h-3 rounded-full border-2 border-white"
              style={{
                background: i === crossings.length - 1 ? '#f97316' : '#d1a87a',
                top: '4px',
              }}
            />
            <div className="flex-1 rounded-lg px-4 py-3" style={{ background: '#fdf8f0', border: '1px solid #ede6db' }}>
              <p className="text-sm font-semibold" style={{ color: '#1f2937' }}>
                {c.toll_plaza_name || c.tollPlazaName || `Toll ${i + 1}`}
              </p>
              <div className="flex items-center gap-3 mt-1 flex-wrap">
                <span className="text-xs" style={{ color: '#6b7280' }}>
                  {fmt(c.reader_read_time || c.readerReadTime)}
                </span>
                {(c.lane_direction || c.laneDirection) && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ background: '#fef3c7', color: '#92400e' }}>
                    {c.lane_direction || c.laneDirection}
                  </span>
                )}
              </div>
              {(c.toll_plaza_geocode || c.tollPlazaGeocode) && (
                <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>
                  📍 {c.toll_plaza_geocode || c.tollPlazaGeocode}
                </p>
              )}
            </div>
          </div>
        ))}
        {/* current position indicator */}
        <div className="relative flex gap-4 items-start pl-10">
          <div
            className="absolute left-2 w-4 h-4 rounded-full border-2 border-orange-400 animate-pulse"
            style={{ background: '#fff7ed', top: '2px' }}
          />
          <div className="flex-1 rounded-lg px-4 py-2" style={{ border: '1px dashed #f97316' }}>
            <p className="text-xs font-medium" style={{ color: '#f97316' }}>🚛 In transit — next toll update in ~45 min</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── main component ──────────────────────────────────────────────────────────

const LiveTrackingTab = ({ bookingId }) => {
  const { user } = useAuth();

  // ── List view state (when no bookingId selected) ─────────────────────────
  const [shipments, setShipments] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [preset, setPreset] = useState('this_fy');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // ── Single booking tracking state ────────────────────────────────────────
  const [booking, setBooking]                   = useState(null);
  const [trackingPayment, setTrackingPayment]   = useState(null);
  const [crossings, setCrossings]               = useState([]);
  const [loadingBooking, setLoadingBooking]     = useState(false);
  const [loadingCrossings, setLoadingCrossings] = useState(false);
  const [paymentLoading, setPaymentLoading]     = useState(false);
  const [error, setError]                       = useState(null);
  const [shareStatus, setShareStatus]           = useState('');

  // ── Load shipments list ──────────────────────────────────────────────────
  const loadShipments = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const userEmail = session?.user?.email || user?.email;
      const userId = session?.user?.id || user?.id;

      const { data, error: fetchErr } = await supabase
        .from('buyer_bookings')
        .select('id, readable_booking_id, factory_name, grade, quantity, assigned_vehicle_number, booking_type, status, created_at, buyer_email, buyer_id')
        .in('status', ['in_transit', 'approved', 'do_generated'])
        .order('created_at', { ascending: false });

      if (fetchErr) throw fetchErr;

      const filtered = (data || []).filter(b =>
        b.buyer_email === userEmail || b.buyer_id === userId
      );
      setShipments(filtered);
    } catch (err) {
      console.error('LiveTrackingTab list error:', err?.message);
      setShipments([]);
    } finally {
      setLoadingList(false);
    }
  }, [user]);

  // ── fetch booking + tracking_payment ────────────────────────────────────
  const fetchBooking = useCallback(async () => {
    if (!bookingId) return;
    setLoadingBooking(true);
    setError(null);

    try {
      const { data: b, error: bErr } = await supabase
        .from('buyer_bookings')
        .select('id, readable_booking_id, readable_offer_id, factory_name, grade, quantity, rate_per_ton, booking_type, loading_from, destination, assigned_vehicle_number, driver_mobile, status, created_at')
        .eq('id', bookingId)
        .maybeSingle();

      if (bErr) throw bErr;
      if (!b) { setError('Booking not found.'); return; }
      setBooking(b);

      // fetch tracking_payment for this booking
      const { data: tp } = await supabase
        .from('tracking_payments')
        .select('id, status, amount, expires_at, activated_at, vehicle_number, readable_booking_id, razorpay_order_id')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      setTrackingPayment(tp || null);
    } catch (e) {
      setError(e.message || 'Failed to load booking.');
    } finally {
      setLoadingBooking(false);
    }
  }, [bookingId]);

  // ── fetch toll crossings ─────────────────────────────────────────────────
  const fetchCrossings = useCallback(async (vehicleNumber) => {
    if (!vehicleNumber) return;
    setLoadingCrossings(true);
    try {
      const { data } = await supabase
        .from('toll_crossings')
        .select('*')
        .eq('vehicle_number', vehicleNumber.toUpperCase())
        .order('reader_read_time', { ascending: true });
      setCrossings(data || []);
    } catch {
      setCrossings([]);
    } finally {
      setLoadingCrossings(false);
    }
  }, []);

  useEffect(() => {
    if (!bookingId) {
      loadShipments();
    } else {
      fetchBooking();
    }
  }, [bookingId, loadShipments, fetchBooking]);

  // once booking loaded and tracking is active, fetch crossings
  useEffect(() => {
    const vehicleNo = booking?.assigned_vehicle_number;
    const isActive = isSugarTruck(booking?.booking_type)
      ? true
      : trackingPayment?.status === 'paid';
    if (vehicleNo && isActive) {
      fetchCrossings(vehicleNo);
    }
  }, [booking, trackingPayment, fetchCrossings]);

  // ── Razorpay payment ─────────────────────────────────────────────────────
  const handlePayAndTrack = async () => {
    if (!booking) return;
    setPaymentLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-razorpay-order`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            booking_id: booking.id,
            readable_booking_id: booking.readable_booking_id,
            paid_by_mobile: user?.mobile_phone || '',
            paid_by_name: user?.full_name || '',
          }),
        }
      );

      const orderData = await res.json();

      if (!res.ok || orderData.error) {
        if (orderData.already_paid) {
          await fetchBooking();
          return;
        }
        throw new Error(orderData.error || 'Could not create payment order.');
      }

      const rzp = new window.Razorpay({
        key: RAZORPAY_KEY,
        order_id: orderData.order_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'SugarHathi',
        description: orderData.description,
        prefill: {
          contact: orderData.prefill?.contact || '',
          name: orderData.prefill?.name || '',
        },
        theme: { color: '#f97316' },
        handler: async (response) => {
          // verify payment
          const verifyRes = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-razorpay-payment`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                paid_by_mobile: user?.mobile_phone || '',
              }),
            }
          );
          const verifyData = await verifyRes.json();
          if (verifyData.success || verifyData.verified) {
            await fetchBooking();
          } else {
            setError('Payment verification failed. Contact support.');
          }
        },
        modal: {
          ondismiss: () => setPaymentLoading(false),
        },
      });

      rzp.open();
    } catch (e) {
      setError(e.message || 'Payment failed. Try again.');
    } finally {
      setPaymentLoading(false);
    }
  };

  // ── Share tracking link ──────────────────────────────────────────────────
  const handleShare = async () => {
    if (!vehicleNo) return;
    const url = `https://sugarhathi.com/track?v=${booking.assigned_vehicle_number}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'SugarHathi Shipment Tracking',
          text: `Track your sugar shipment — Vehicle ${vehicleNo}`,
          url,
        });
        setShareStatus('Shared!');
      } catch {
        // user cancelled — silent
      }
    } else {
      await navigator.clipboard.writeText(url);
      setShareStatus('Link copied!');
    }
    setTimeout(() => setShareStatus(''), 2500);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // No booking selected — show list with search + date filter
  if (!bookingId) {
    const { from: dateFrom, to: dateTo } = getDateRange(preset, customFrom, customTo);

    const filteredShipments = shipments.filter(s => {
      const q = searchText.toLowerCase();
      const matchesSearch = !q ||
        s.factory_name?.toLowerCase().includes(q) ||
        s.assigned_vehicle_number?.toLowerCase().includes(q) ||
        s.readable_booking_id?.toLowerCase().includes(q);

      const shipmentDate = s.created_at ? new Date(s.created_at) : null;
      const matchesDate =
        (!dateFrom && !dateTo) ||
        (shipmentDate &&
          (!dateFrom || shipmentDate >= dateFrom) &&
          (!dateTo || shipmentDate <= dateTo));

      return matchesSearch && matchesDate;
    });

    return (
      <div className="space-y-5">
        {/* Header */}
        <div>
          <h2 className="text-xl font-bold" style={{ color: '#1a1a1a' }}>Live Tracking</h2>
          <p className="text-sm mt-0.5" style={{ color: '#9ca3af' }}>
            {loadingList
              ? 'Loading...'
              : `${filteredShipments.length} shipment${filteredShipments.length !== 1 ? 's' : ''} trackable`}
          </p>
        </div>

        {/* Search + Date Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon name="Search" size={16} color="#9ca3af" />
            </span>
            <input
              type="text"
              placeholder="Search by factory, vehicle, or booking ID..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ border: '1px solid #e8ddd0', color: '#1e293b', backgroundColor: '#ffffff' }}
            />
          </div>
          <DateRangeFilter
            preset={preset}
            onPresetChange={setPreset}
            customFrom={customFrom}
            onCustomFromChange={setCustomFrom}
            customTo={customTo}
            onCustomToChange={setCustomTo}
          />
        </div>

        {/* Loading */}
        {loadingList && (
          <div className="text-center py-12 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
            <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm" style={{ color: '#9ca3af' }}>Loading shipments...</p>
          </div>
        )}

        {/* Empty state */}
        {!loadingList && filteredShipments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 rounded-2xl"
            style={{ background: '#fdf8f0', border: '1px dashed #d6c9b8' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background: '#fff3e8', border: '2px solid #f97316' }}>
              <Icon name="Navigation" size={28} color="#f97316" />
            </div>
            <p className="text-base font-semibold" style={{ color: '#1f2937' }}>
              {shipments.length === 0 ? 'No active shipments' : 'No matching shipments'}
            </p>
            <p className="text-sm mt-1 text-center max-w-xs" style={{ color: '#6b7280' }}>
              {shipments.length === 0
                ? 'Approved and in-transit bookings will appear here'
                : 'Try adjusting your search or date filter'}
            </p>
          </div>
        )}

        {/* Shipment cards */}
        {!loadingList && filteredShipments.length > 0 && (
          <div className="space-y-4">
            {filteredShipments.map(s => {
              const statusStyles = {
                in_transit: { bg: '#dbeafe', text: '#1d4ed8' },
                approved: { bg: '#dcfce7', text: '#16a34a' },
                do_generated: { bg: '#f3e8ff', text: '#7c3aed' },
              };
              const ss = statusStyles[s.status] || { bg: '#f3f4f6', text: '#6b7280' };

              return (
                <div key={s.id} className="rounded-xl p-5 transition-all duration-200"
                  style={{ background: '#ffffff', border: '1px solid #e8ddd0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: '#fff3e8' }}>
                        <Icon name="Truck" size={20} color="#f97316" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm" style={{ color: '#1e293b' }}>{s.factory_name || '—'}</p>
                        <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>{s.readable_booking_id || s.id}</p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={{ background: ss.bg, color: ss.text }}>
                      {s.status?.replace(/_/g, ' ')}
                    </span>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="rounded-lg px-3 py-2" style={{ background: '#f3ede3' }}>
                      <p className="text-xs mb-0.5" style={{ color: '#9ca3af' }}>Grade</p>
                      <p className="text-sm font-semibold" style={{ color: '#374151' }}>{s.grade || '—'}</p>
                    </div>
                    <div className="rounded-lg px-3 py-2" style={{ background: '#f3ede3' }}>
                      <p className="text-xs mb-0.5" style={{ color: '#9ca3af' }}>Qty</p>
                      <p className="text-sm font-semibold" style={{ color: '#374151' }}>{s.quantity} MT</p>
                    </div>
                    <div className="rounded-lg px-3 py-2" style={{ background: '#f3ede3' }}>
                      <p className="text-xs mb-0.5" style={{ color: '#9ca3af' }}>Vehicle</p>
                      <p className="text-sm font-semibold truncate" style={{ color: '#374151' }}>
                        {s.assigned_vehicle_number || '—'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t" style={{ borderColor: '#f3ede3' }}>
                    <Icon name="Info" size={13} color="#9ca3af" />
                    <p className="text-xs" style={{ color: '#9ca3af' }}>
                      Go to <strong style={{ color: '#f97316' }}>Active Bookings</strong> → tap <strong style={{ color: '#f97316' }}>Track Truck</strong> to start tracking
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Single booking tracking detail view — completely unchanged below
  // ─────────────────────────────────────────────────────────────────────────

  // Loading booking
  if (loadingBooking) {
    return (
      <div className="text-center py-20">
        <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm" style={{ color: '#9ca3af' }}>Loading booking details...</p>
      </div>
    );
  }

  // Error
  if (error && !booking) {
    return (
      <div className="text-center py-16 rounded-xl" style={{ background: '#fef2f2', border: '1px solid #fecaca' }}>
        <Icon name="AlertCircle" size={32} color="#ef4444" />
        <p className="mt-2 text-sm font-medium text-red-600">{error}</p>
      </div>
    );
  }

  if (!booking) return null;

  const isTruck  = isSugarTruck(booking.booking_type);
  const isPaid   = isTruck || trackingPayment?.status === 'paid';
  const isPending = !isTruck && (!trackingPayment || trackingPayment.status === 'pending');
  const vehicleNo = booking.assigned_vehicle_number;
  const rid = booking.readable_booking_id || booking.id;

  return (
    <div className="space-y-5">

      {/* ── Page header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ color: '#1a1a1a' }}>Live Tracking</h2>
          <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
            Booking <span className="font-semibold text-orange-500">{rid}</span>
          </p>
        </div>
        {isPaid && (
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={{ background: '#fff3e8', color: '#f97316', border: '1px solid #fed7aa' }}
          >
            <Icon name="Share2" size={15} />
            {shareStatus || 'Share Link'}
          </button>
        )}
      </div>

      {/* ── Error banner (non-blocking) ── */}
      {error && booking && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm"
          style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' }}>
          <Icon name="AlertCircle" size={16} />
          {error}
        </div>
      )}

      {/* ── Booking info card ── */}
      <div className="rounded-2xl overflow-hidden shadow-sm" style={{ border: '1px solid #e8ddd0' }}>
        <div className="px-5 py-3 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}>
          <div className="flex items-center gap-2">
            <Icon name="Truck" size={18} color="#fff" />
            <span className="text-sm font-bold text-white">{vehicleNo || 'Vehicle TBD'}</span>
          </div>
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{ background: 'rgba(255,255,255,0.25)', color: '#fff' }}>
            {isTruck ? 'Sugar + Truck' : 'Sugar Only'}
          </span>
        </div>

        <div className="px-5 py-4 bg-white">
          <InfoRow label="Factory" value={booking.factory_name} />
          <InfoRow label="Grade" value={booking.grade} />
          <InfoRow label="Quantity" value={`${booking.quantity} MT`} />
          <InfoRow label="Loading From" value={booking.loading_from} />
          <InfoRow label="Destination" value={booking.destination} />
          {booking.driver_mobile && (
            <InfoRow label="Driver Mobile" value={booking.driver_mobile} />
          )}
        </div>
      </div>

      {/* ── Payment gate — Sugar Only + pending ── */}
      {isPending && (
        <div className="rounded-2xl p-6 text-center"
          style={{ background: '#fffbf5', border: '2px dashed #fed7aa' }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ background: '#fff3e8' }}>
            <Icon name="Lock" size={26} color="#f97316" />
          </div>
          <p className="text-base font-bold mb-1" style={{ color: '#1f2937' }}>Activate Tracking</p>
          <p className="text-sm mb-5" style={{ color: '#6b7280' }}>
            One-time payment of <strong>₹199</strong> to track this shipment via FASTag toll data.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handlePayAndTrack}
              disabled={paymentLoading || !vehicleNo}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white transition-all disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
            >
              {paymentLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Icon name="CreditCard" size={16} />
              )}
              {paymentLoading ? 'Opening payment...' : 'Pay ₹199 & Track'}
            </button>

            <button
              onClick={handleShare}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold transition-all"
              style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}
            >
              <Icon name="Share2" size={16} />
              {shareStatus || 'Share Tracking Link'}
            </button>
          </div>

          {!vehicleNo && (
            <p className="text-xs mt-3" style={{ color: '#f97316' }}>
              Vehicle number not assigned yet — payment will be enabled once vehicle is assigned.
            </p>
          )}
        </div>
      )}

      {/* ── Tracking active — toll timeline ── */}
      {isPaid && (
        <div className="rounded-2xl overflow-hidden shadow-sm" style={{ border: '1px solid #e8ddd0' }}>
          <div className="px-5 py-3 flex items-center justify-between"
            style={{ background: '#fffbf5', borderBottom: '1px solid #ede6db' }}>
            <div className="flex items-center gap-2">
              <Icon name="Navigation" size={16} color="#f97316" />
              <span className="text-sm font-bold" style={{ color: '#1f2937' }}>Toll Crossings</span>
            </div>
            <div className="flex items-center gap-3">
              {isTruck && (
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                  style={{ background: '#d1fae5', color: '#065f46' }}>
                  Free — Sugar+Truck
                </span>
              )}
              {!isTruck && trackingPayment?.expires_at && (
                <span className="text-xs" style={{ color: '#9ca3af' }}>
                  Expires {fmtDate(trackingPayment.expires_at)}
                </span>
              )}
              <button
                onClick={() => fetchCrossings(vehicleNo)}
                className="p-1.5 rounded-lg transition-colors hover:bg-orange-50"
                title="Refresh"
              >
                <Icon name="RefreshCw" size={14} color="#f97316" />
              </button>
            </div>
          </div>

          <div className="px-5 py-5 bg-white">
            <TollTimeline crossings={crossings} loading={loadingCrossings} />
          </div>

          {/* Share row at bottom */}
          <div className="px-5 py-3 flex items-center justify-between"
            style={{ background: '#fdf8f0', borderTop: '1px solid #ede6db' }}>
            <p className="text-xs" style={{ color: '#9ca3af' }}>
              FASTag data updates every ~45 minutes
            </p>
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all"
              style={{ background: '#fff3e8', color: '#f97316', border: '1px solid #fed7aa' }}
            >
              <Icon name="Share2" size={12} />
              {shareStatus || 'Share Link'}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default LiveTrackingTab;
